import { Subject, BehaviorSubject } from 'rxjs';
import { FormChangesService } from '@awp/forms';
export var StateChanged;
(function (StateChanged) {
    StateChanged["VisibilityChange"] = "visibilityChange";
    StateChanged["InfoPanelChange"] = "infoPanelVisibilityChange";
    StateChanged["Completed"] = "completed";
    StateChanged["Visited"] = "visited";
    StateChanged["Submitted"] = "submitted";
    StateChanged["Reset"] = "reset";
    StateChanged["Active"] = "active";
    StateChanged["NextVisible"] = "next_visible";
    StateChanged["NextHidden"] = "next_hidden";
    StateChanged["NextEnabled"] = "next_enabled";
    StateChanged["NextDisabled"] = "next_disabled";
})(StateChanged || (StateChanged = {}));
var StepperStateService = /** @class */ (function () {
    function StepperStateService(formChanges) {
        this.formChanges = formChanges;
        this._nextActivatableStep = null;
        this._visibleSteps = new Set();
        this._visibleInfoSteps = new Set();
        this._completedSteps = new Set();
        this._visitedSteps = new Set();
        this._submittedSteps = new Set();
        this._stateChanges = new Subject();
        this.stateChanges = this._stateChanges.asObservable();
        this._nextVisible = new BehaviorSubject(true);
        this.nextVisible = this._nextVisible.asObservable();
        this._nextEnabled = new BehaviorSubject(true);
        this.nextEnabled = this._nextEnabled.asObservable();
        this.allowDisableNext = true;
        this._isVisible = true;
        this._isNextEnabled = false;
    }
    StepperStateService.prototype.reset = function (state, notify) {
        if (state === void 0) { state = { completed: [], visited: [], submitted: [] }; }
        if (notify === void 0) { notify = true; }
        this._nextActivatableStep = null;
        this._completedSteps = new Set(state.completed || []);
        this._visitedSteps = new Set(state.visited || []);
        this._submittedSteps = new Set(state.submitted || []);
        if (notify) {
            this._stateChanges.next({ state: StateChanged.Reset });
        }
    };
    StepperStateService.prototype.markStepAsNextActivatable = function (id) {
        this._nextActivatableStep = id;
    };
    StepperStateService.prototype.markStepAsCompleted = function (id) {
        this._completedSteps.add(id);
        this._stateChanges.next({ state: StateChanged.Completed, id: id });
    };
    StepperStateService.prototype.markStepAsVisited = function (id) {
        this._visitedSteps.add(id);
        this._stateChanges.next({ state: StateChanged.Visited, id: id });
    };
    StepperStateService.prototype.markStepAsSubmitted = function (id) {
        this._submittedSteps.add(id);
        this._stateChanges.next({ state: StateChanged.Submitted, id: id });
        this.formChanges.markAsTouched();
    };
    StepperStateService.prototype.isComplete = function (id) {
        return this._completedSteps.has(id);
    };
    StepperStateService.prototype.hasSubmitted = function (id) {
        return this._submittedSteps.has(id);
    };
    StepperStateService.prototype.hasVisited = function (id) {
        return this._visitedSteps.has(id);
    };
    StepperStateService.prototype.changeActiveStep = function (id) {
        this._activeStepId = id;
        this._stateChanges.next({ state: StateChanged.Active, id: id });
        if (this.hasSubmitted(id)) {
            this.formChanges.markAsTouched();
        }
    };
    Object.defineProperty(StepperStateService.prototype, "activeStepId", {
        get: function () {
            return this._activeStepId;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(StepperStateService.prototype, "isNextVisible", {
        get: function () {
            return this._isVisible;
        },
        enumerable: true,
        configurable: true
    });
    StepperStateService.prototype.showNext = function () {
        this._isVisible = true;
        this._nextVisible.next(this._isVisible);
        this._stateChanges.next({ state: StateChanged.NextVisible });
    };
    StepperStateService.prototype.hideNext = function () {
        this._isVisible = false;
        this._nextVisible.next(this._isVisible);
        this._stateChanges.next({ state: StateChanged.NextHidden });
    };
    Object.defineProperty(StepperStateService.prototype, "isNextEnabled", {
        get: function () {
            return this._isNextEnabled;
        },
        enumerable: true,
        configurable: true
    });
    StepperStateService.prototype.enableNext = function () {
        this._isNextEnabled = true;
        this._nextEnabled.next(this._isNextEnabled);
        this._stateChanges.next({ state: StateChanged.NextEnabled });
    };
    StepperStateService.prototype.disableNext = function () {
        this._isNextEnabled = !this.allowDisableNext;
        this._nextEnabled.next(this._isNextEnabled);
        this._stateChanges.next({ state: StateChanged.NextDisabled });
    };
    StepperStateService.prototype.getStepState = function (id, activeStepId, activeParentStepId) {
        if (id === activeStepId || id === activeParentStepId) {
            return 'active';
        }
        else if (this.isComplete(id)) {
            return 'completed';
        }
        else {
            return 'none';
        }
    };
    StepperStateService.prototype.setVisibleSteps = function (ids) {
        this._visibleSteps = new Set(ids);
        this._stateChanges.next({ state: StateChanged.VisibilityChange });
    };
    StepperStateService.prototype.showStep = function (id) {
        this._visibleSteps.add(id);
        this._stateChanges.next({ id: id, state: StateChanged.VisibilityChange });
    };
    StepperStateService.prototype.hideStep = function (id) {
        this._visibleSteps.delete(id);
        this._stateChanges.next({ id: id, state: StateChanged.VisibilityChange });
    };
    StepperStateService.prototype.isStepVisible = function (id) {
        return this._visibleSteps.has(id);
    };
    StepperStateService.prototype.hasVisibleSteps = function () {
        return this._visibleSteps.size > 0;
    };
    StepperStateService.prototype.showStepInfo = function (id) {
        this._visibleSteps.add(id);
        this._stateChanges.next({ id: id, state: StateChanged.InfoPanelChange });
    };
    StepperStateService.prototype.hideStepInfo = function (id) {
        this._visibleSteps.delete(id);
        this._stateChanges.next({ id: id, state: StateChanged.InfoPanelChange });
    };
    StepperStateService.prototype.isStepInfoVisible = function (id) {
        return this._visibleInfoSteps.has(id);
    };
    StepperStateService.prototype.isMarkedAsNextActivatableStep = function (id) {
        return this._nextActivatableStep === id;
    };
    return StepperStateService;
}());
export { StepperStateService };
